/**
 * 操作台鍵盤翻頁（REQ-20260418015）。
 * 對齊原系統 js/main.js 的 keyCode 107（+）/ 109（-）行為：
 *   + → 下一筆
 *   - → 上一筆
 *
 * 不觸發條件：
 * - 使用者正在 input / textarea / select 內輸入（避免吃掉字元）
 * - contenteditable 元素內
 * - Modal / Dialog 開啟時（由 activeElement 在 dialog 內判斷）
 */
import { useEffect } from 'react';

export function useNeighborHotkeys(handlers: {
  onNext: () => void;
  onPrevious: () => void;
  enabled?: boolean;
}): void {
  const { onNext, onPrevious, enabled = true } = handlers;
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // 支援主鍵盤 + / - 與 numpad（Firefox 用 e.key; 所有瀏覽器都可讀 '+' '-'）
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        onNext();
      } else if (e.key === '-') {
        e.preventDefault();
        onPrevious();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onNext, onPrevious, enabled]);
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  // Modal 內（dialog role）也視為輸入中，讓 Modal 自己處理鍵盤
  if (target.closest('[role="dialog"]')) return true;
  return false;
}
